import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { bankersRounding } from 'utils/betslip/bankersRounding';
import { ETaxTypes } from 'interfaces/betslip';
import { BetslipCalculationField } from 'newelements/_Betslip/hooks/generateFieldsMapping/classes';
import { useFormatAmount } from 'newelements/_Betslip/hooks/generateFieldsMapping/hooks/shared/useFormatAmount';
import { TUseGenerateBetslipFieldsReturnType } from 'newelements/_Betslip/hooks/generateFieldsMapping/types';
import { addSeparatorAndCut } from 'newelements/_Betslip/hooks/generateFieldsMapping/utils';
import { TCalculationParams } from 'newelements/_Betslip/utils/calculator';
import {
  calculateBasicTaxes,
  calculateRangeTaxes
} from 'newelements/_Betslip/utils/calculator/calculateTaxes';
import { roundMultipleAmount } from 'newelements/_Betslip/utils/roundings/round-amount';
import { getRoundingLoading } from 'store/selectors/app-data';
import { getTaxes } from 'store/selectors/betslip';

export const UseGenerateBetBuilderFooterFields = (
  stake: number,
  odd: number,
  sportId?: number
) => {
  const { t } = useTranslation();
  const formatAmount = useFormatAmount();
  const taxes = useSelector(getTaxes);
  const roundingLoading = useSelector(getRoundingLoading);

  const basicTaxParams: TCalculationParams = {
    stake: stake,
    odd: odd,
    isEachWay: false,
    eventId: '',
    sportIds: sportId ? [sportId] : []
  };

  const basicTaxesResult = calculateBasicTaxes(basicTaxParams);

  const rangeTaxParams = {
    stake: basicTaxesResult.result.stake,
    possibleWin: basicTaxesResult.result.possibleWin
  };

  const rangeTaxResult = calculateRangeTaxes(rangeTaxParams);

  const fields = useMemo(() => {
    const result: TUseGenerateBetslipFieldsReturnType = [];
    const isTaxForStake = taxes.basic?.type === ETaxTypes.STAKE_TAX;

    result.push(
      new BetslipCalculationField(
        t('account.stake'),
        formatAmount(addSeparatorAndCut(stake)),
        {
          'data-testid': `betBuilder-stake`
        }
      )
    );

    const isBasicTaxesAvailable =
      taxes.basic?.type && basicTaxesResult.taxes?.isTaxable;

    if (isBasicTaxesAvailable && isTaxForStake) {
      if (taxes.basic?.percent) {
        const translation = t('betslip.taxAmount');
        result.push(
          new BetslipCalculationField(
            `${translation} (${taxes.basic?.percent}%)`,
            formatAmount(
              addSeparatorAndCut(basicTaxesResult.taxes.taxAmount || 0)
            ),
            {
              'data-testid': 'betBuilder-taxAmount'
            }
          )
        );
      }

      const isPossibleWinTax =
        taxes?.basic?.type &&
        [
          ETaxTypes.POSSIBLE_WIN_TAX,
          ETaxTypes.POSSIBLE_WIN_TAX_WITHOUT_STAKE
        ].includes(taxes.basic.type);

      if (isPossibleWinTax) {
        const translation = t('betslip.taxBonus');
        result.push(
          new BetslipCalculationField(
            `${translation} (${taxes?.basic?.percent}%)`,
            formatAmount(
              addSeparatorAndCut(basicTaxesResult.taxes?.taxAmount || 0)
            ),
            {
              'data-testid': 'betBuilder-taxBonus'
            }
          )
        );
      }
    }

    if (isTaxForStake) {
      result.push(
        new BetslipCalculationField(
          t('account.totalStake'),
          formatAmount(
            addSeparatorAndCut(
              bankersRounding(basicTaxesResult.result.stake || 0)
            )
          ),
          {
            'data-testid': 'betBuilder-totalStake'
          }
        )
      );
    }

    return result;
  }, [taxes, basicTaxesResult, stake, odd]);

  const totalReturnFields = useMemo(() => {
    const result: TUseGenerateBetslipFieldsReturnType = [];

    const isTaxesAvailable =
      !!taxes.basic?.type && !!basicTaxesResult.taxes?.isTaxable;

    const isTotalReturnAlreadyDisplayedOnFooter =
      isTaxesAvailable &&
      (!Number(rangeTaxResult.result?.possibleWin) ||
        !rangeTaxResult.taxes?.taxAmount);

    // @Todo need to understand
    if (!isTotalReturnAlreadyDisplayedOnFooter) {
      const value = formatAmount(
        addSeparatorAndCut(
          roundMultipleAmount(
            rangeTaxResult.result.possibleWinWithoutRangeTax || 0
          )
        )
      );

      result.push(
        new BetslipCalculationField(
          t('betslip.totalReturn'),
          value,
          {
            'data-testid': 'totalReturn'
          },
          roundingLoading
        )
      );

      if (isTaxesAvailable) {
        const isProfitTax =
          !!rangeTaxResult.taxes?.rangeTaxedPossibleWinWithoutStakeTaxAmount;

        const translation = isProfitTax
          ? 'betslip.taxProfit'
          : 'betslip.taxAmount';

        const rangeTaxAmount =
          rangeTaxResult.taxes?.rangeTaxedPossibleWinTaxAmount ??
          rangeTaxResult.taxes?.rangeTaxedPossibleWinWithoutStakeTaxAmount ??
          0;

        const value = formatAmount(addSeparatorAndCut(rangeTaxAmount));

        result.push(
          new BetslipCalculationField(
            `${t(translation)} (${rangeTaxResult.taxes?.taxPercent}%)`,
            value,
            {
              'data-testid': `betBuilder-${translation.replace('betslip.', '')}`
            },
            roundingLoading
          )
        );
      }
    }

    if (isTaxesAvailable) {
      const translation = isTotalReturnAlreadyDisplayedOnFooter
        ? 'betslip.possibleWin'
        : 'betslip.finalPayout';

      const daraTestId = isTotalReturnAlreadyDisplayedOnFooter
        ? 'totalReturn'
        : 'finalPayout';

      const value = formatAmount(
        addSeparatorAndCut(
          roundMultipleAmount(rangeTaxResult.result.possibleWin ?? 0)
        )
      );

      result.push(
        new BetslipCalculationField(
          t(translation),
          value,
          {
            'data-testid': daraTestId
          },
          roundingLoading
        )
      );
    }

    return result;
  }, [
    roundingLoading,
    rangeTaxResult,
    basicTaxesResult,
    taxes,
    formatAmount,
    stake,
    odd,
    sportId
  ]);

  return [...fields, ...totalReturnFields];
};
